import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { Button } from "./ui/button";
import { Label } from "./ui/label";

/**
 * LC2 (ROADMAP, opdracht Frank 30 oktober 2026): de meetset voor code, per model.
 *
 * Per model op deze Mac staat hier hoeveel van de vijftien codeopdrachten ECHT lukken: de code van het model
 * doorstaat de toets in de sandbox (`backend/services/gouden_code.py`). De weekmeting meet elke zondagnacht;
 * "Meet nu" meet meteen, op de achtergrond. Er wordt niets gedownload.
 *
 * **ZONDER ANTWOORD VAN DE SERVER STAAT ER NIETS**, net als bij de weekmeting: een lege lijst die eigenlijk
 * "onbekend" betekent, leest als "nog nooit gemeten".
 */
export const OPNIEUW_MS = 10000;

export function datum(iso, taal) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(taal === "en" ? "en-GB" : "nl-NL", {
    timeZone: "Europe/Amsterdam", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

export default function CodeMeetset() {
  const { t, taal } = useTaal();
  const [data, setData] = useState(null);
  const [reden, setReden] = useState("");
  const [open, setOpen] = useState(false);
  const klok = useRef(null);

  const laad = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/ollama/codemeting`);
      // Een antwoord zonder de twee lijsten (een oudere backend) telt als geen antwoord: zonder deze eis viel het
      // hele venster Instellingen om op `modellen.length`.
      const geldig = Array.isArray(d?.modellen) && Array.isArray(d?.opdrachten);
      setData(geldig ? d : null);
      return geldig ? d : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    laad();
    return () => clearTimeout(klok.current);
  }, [laad]);

  // Zolang er gemeten wordt, kijkt het scherm elke tien seconden opnieuw.
  useEffect(() => {
    clearTimeout(klok.current);
    if (data?.bezig) klok.current = setTimeout(laad, OPNIEUW_MS);
  }, [data, laad]);

  async function meetNu() {
    setReden("");
    try {
      const { data: d } = await axios.post(`${API}/ollama/codemeting/nu`);
      if (!d.gestart && d.reden) setReden(t(d.reden));
    } catch (error) {
      const detail = error.response?.data?.detail;
      setReden(typeof detail === "string" ? detail : t("De meting is niet gestart."));
    }
    laad();
  }

  if (!data) return null;
  const totaal = data.opdrachten?.length || 0;
  return (
    <div className="space-y-2" data-testid="code-meetset">
      <Label>{t("Meetset voor code")}</Label>
      <p className="text-xs text-muted-foreground">
        {t("Per model: hoeveel van de {n} codeopdrachten echt lukken. Een opdracht lukt alleen als de code de toets in de sandbox doorstaat.", { n: totaal })}
      </p>
      {data.modellen.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="code-meetset-leeg">{t("Nog niet gemeten.")}</p>
      ) : (
        <ul className="space-y-1 text-sm" data-testid="code-meetset-modellen">
          {data.modellen.map((m) => (
            <li key={m.model} className="flex items-baseline justify-between gap-2" data-testid={`code-meetset-${m.model}`}>
              <span className="font-mono text-xs truncate">{m.model}</span>
              <span>
                {t("{a} van {b} lukken", { a: m.geslaagd, b: m.totaal })}
                <span className="ml-2 text-xs text-muted-foreground">{datum(m.gemeten_op, taal)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={meetNu} disabled={data.bezig}
                data-testid="code-meetset-nu">
          {data.bezig ? t("Bezig met meten...") : t("Meet nu")}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen((o) => !o)}
                data-testid="code-meetset-opdrachten-knop">
          {open ? t("Verberg de opdrachten") : t("Toon de opdrachten")}
        </Button>
      </div>
      {reden && <p className="text-xs text-destructive" data-testid="code-meetset-reden">{reden}</p>}
      {open && (
        <ol className="list-decimal pl-5 space-y-1 text-xs" data-testid="code-meetset-opdrachten">
          {data.opdrachten.map((o) => (
            <li key={o.id}>
              {o.opdracht}
              <div className="text-muted-foreground">{t("Herkomst: {bron}", { bron: o.bron })}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
